/* lib/aiAccess.js — «нет доступа → нет модели»: чистая логика для экранов.

   Правило владельца: у неактивного пространства (access.state ∈ no_plan |
   trial_over | expired) ни один запрос не доходит до LLM. Бэкенд закрывает
   каждый AI-вход ответом 402 {detail, code:'subscription_expired', state};
   фоновая сборка дерева останавливается с `stop_reason:'subscription_expired'`
   (узлы ждут в `waiting`, после оплаты `resume` продолжает); commit черновика
   создаёт сущность, но говорит `tree_build_skipped:'subscription_expired'`;
   отчёт v2 сохраняет расчётную часть с `ai_skipped:'subscription_expired'`.

   Раньше экраны превращали это в глухое «Не удалось…» или — хуже — в зелёное
   «Добавлено в систему». Здесь ОДНО сопоставление «код → текст + куда вести»;
   экраны его не копируют.

   Инвариант (близнец planAccess.js / planLock.js): нет ключа, другой код или
   старый бэкенд → null/false, экран ведёт себя ровно как раньше. */

export const SUBSCRIPTION_EXPIRED = 'subscription_expired';

// Запасной текст, если 402 пришёл без detail (detail бэкенда всегда главнее).
export const AI_CLOSED_FALLBACK = 'AI-функции входят в платный тариф. Выберите тариф, чтобы продолжить.';

const TREE_STOP_NOTE = 'Сборка рецептов остановлена: AI-функции недоступны без активного тарифа. '
  + 'Выберите тариф и нажмите «Достроить».';
const TREE_SKIPPED_NOTE = 'Создано, но рецепты и составы не собраны: AI-функции недоступны без активного тарифа.';
const REPORT_AI_SKIPPED_NOTE = 'Диагностика AI не выполнена: доступ к модели закрыт — выберите тариф';

/* Код ошибки из любой формы, в которой он доходит до экрана:
   ошибка unwrap ({status, code, detail}), сырой ответ apiFetch ({ok,status,data}),
   тело ответа или SSE-кадр ({type:'error', code, detail}). */
function codeOf(x) {
  if (!x || typeof x !== 'object') return null;
  if (x.code) return x.code;
  if (x.data && typeof x.data === 'object' && x.data.code) return x.data.code;
  return null;
}

function detailOf(x) {
  if (!x || typeof x !== 'object') return null;
  if (typeof x.detail === 'string' && x.detail) return x.detail;
  if (x.data && typeof x.data === 'object' && typeof x.data.detail === 'string' && x.data.detail) return x.data.detail;
  return null;
}

/* Закрыт ли доступ к модели (402 subscription_expired). */
export function isAiClosed(x) {
  return codeOf(x) === SUBSCRIPTION_EXPIRED;
}

/* Текст для пользователя, если доступ закрыт; иначе null — экран оставляет свой. */
export function aiClosedDetail(x) {
  if (!isAiClosed(x)) return null;
  return detailOf(x) || AI_CLOSED_FALLBACK;
}

/* Куда ведёт «Выбрать тариф» (как в FE-1 и пузырях чата): бизнес-пространство —
   тариф бизнеса (квота и оплата общие на пространство), личное — свой тариф. */
export function tariffTarget(workspace) {
  return workspace && workspace.is_business
    ? '/settings?tab=business&biz=tariff'
    : '/settings?tab=subscription';
}

/* Причина остановки сборки дерева. Принимает и ответ бэкенда (`stop_reason`),
   и снимок провайдера чата (`stopReason`). Ручная «Стоп» (reason '') → null. */
export function treeStopNote(job) {
  if (!job || typeof job !== 'object') return null;
  const reason = job.stop_reason !== undefined ? job.stop_reason : job.stopReason;
  return reason === SUBSCRIPTION_EXPIRED ? TREE_STOP_NOTE : null;
}

/* Ответ commit черновика: сущность создана, а сборку дерева бэкенд не запускал. */
export function treeSkippedNote(res) {
  return res && res.tree_build_skipped === SUBSCRIPTION_EXPIRED ? TREE_SKIPPED_NOTE : null;
}

/* Закрыт ли доступ к модели по СОСТОЯНИЮ ПОДПИСКИ — заранее, без 402 (kitchen-ux M1).
   access — `subscription.access` {ok, state} из /api/subscription/me/
   (lib/adapters/subscription.js; тот же источник, что у строки статуса на карточке
   тарифа). Предикат тот же, что у бэкенда: модель не зовут, когда `paid_access` не ok
   (subscriptions.ai_gate.closed_state), каким бы ни было состояние.
   Нужен там, где экран говорит ДО запроса: не звать сторожа ради заведомого 402, не говорить
   «ассистент оценивает…» перед отказом. Что карточка создана без оценки, говорит не он, а
   ответ записи (kbjuSkipped ниже).
   Инвариант модуля: ключа нет (старый бэкенд, ответ ещё не пришёл) или доступ открыт →
   false, экран делает ровно как раньше. */
export function isAccessClosed(access) {
  return !!access && typeof access === 'object' && access.ok === false;
}

/* Доступ к модели ИЗВЕСТНО открыт (`access.ok === true`, грейс тоже) — только тогда экран
   обещает её работу («Создаём и оцениваем КБЖУ…»). Ответа нет, запрос подписки упал, старый
   бэкенд без ключа — не открыт: обещание, которое не сбудется, хуже его отсутствия (kitchen-ux
   M1-micro: при 500 подписки реестр обещал оценку закрытому пространству). */
export function isAccessOpen(access) {
  return !!access && typeof access === 'object' && access.ok === true;
}

// Тексты 402 AI-входов — зеркало subscriptions/ai_gate._TEXTS[SURFACE_AI] (тест сверяет байт в
// байт); нейтральный — AI_CLOSED_FALLBACK выше.
export const AI_CLOSED_ENDED = 'Оплата тарифа закончилась — AI-функции недоступны. Продлите тариф, чтобы продолжить.';

/* Причина отказа, когда экран заранее знает, что доступ закрыт, и запрос не шлёт (редактор
   ТТК: «Создать (AI)» не зовёт сторожа ради заведомого 402). Тот же текст, что сказал бы
   сервер: «оплата закончилась» — только упавшему клиенту (`expired`), остальным — нейтрально. */
export function aiClosedText(state) {
  return state === 'expired' ? AI_CLOSED_ENDED : AI_CLOSED_FALLBACK;
}

/* Метка ответа записи: карточки сырья созданы БЕЗ авто-оценки КБЖУ — пространство закрыто
   (бэкенд kitchen-ux M2; контракты c1-cards.json и a3-ttk.json, блок «closed workspace (S34)»).
   Ключ верхнего уровня {detail, code, state, cards: [{id, name}]} у POST /api/kitchen/cards/,
   POST /api/ttk/, POST …/rows/, …/rows/bulk/, PATCH …/rows/<id>/ — только когда оценка была
   нужна и пропущена. Экран говорит по ней, а не по догадке из состояния подписки. */
export const KBJU_SKIPPED_KEY = 'kbju_autofill_skipped';

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/* Метка из тела ответа записи или null (ключа нет — оценка не пропускалась). */
export function kbjuSkipped(body) {
  const marker = isPlainObject(body) ? body[KBJU_SKIPPED_KEY] : null;
  return isPlainObject(marker) ? marker : null;
}

/* Метки шагов одной операции («Сохранить» ТТК: PATCH строки, rows/bulk…) — одной меткой:
   карточки всех шагов в порядке создания, без повторов; причина — первой метки. */
export function mergeKbjuSkipped(markers) {
  const list = (Array.isArray(markers) ? markers : []).filter(isPlainObject);
  if (!list.length) return null;
  const seen = new Set();
  const cards = [];
  list.forEach((m) => (Array.isArray(m.cards) ? m.cards : []).forEach((c) => {
    if (!isPlainObject(c)) return;
    const key = c.id != null ? 'id:' + c.id : 'name:' + c.name;
    if (seen.has(key)) return;
    seen.add(key);
    cards.push(c);
  }));
  return { ...list[0], cards };
}

const NAMES_SHOWN = 3;

/* Одно нейтральное сообщение на всю операцию: какие карточки созданы без КБЖУ и почему —
   текстом сервера (detail метки; его нет — запасной текст модуля). Без «Повторить»: повтор
   получил бы тот же пропуск. Метки нет → null. */
// place — хвост о месте (« в прежнем пространстве «…»»): карточка легла, когда вкладка уже
// ушла в другое пространство (реестры «Продукции», QA Г3 I10); по умолчанию — пусто
export function kbjuSkippedNote(marker, place = '') {
  if (!isPlainObject(marker)) return null;
  const cards = Array.isArray(marker.cards) ? marker.cards : [];
  const names = [...new Set(cards.map((c) => (isPlainObject(c) && typeof c.name === 'string' ? c.name.trim() : '')).filter(Boolean))];
  const count = Math.max(cards.length, names.length, 1);
  const shown = names.slice(0, NAMES_SHOWN).map((n) => '«' + n + '»');
  const rest = count - shown.length;
  const list = shown.length ? ' ' + shown.join(', ') + (rest > 0 ? ' и ещё ' + rest : '') : '';
  const where = typeof place === 'string' ? place : '';
  const subject = count === 1
    ? 'Карточка сырья' + list + ' создана' + where + ' без КБЖУ.'
    : 'Карточки сырья' + list + ' созданы' + where + ' без КБЖУ.';
  const detail = typeof marker.detail === 'string' && marker.detail.trim() ? marker.detail.trim() : AI_CLOSED_FALLBACK;
  return subject + ' ' + detail;
}

/* Пометка в отчёте v2: расчёт сохранён, AI-диагностика отказана доступом. */
export function reportAiSkippedNote(result) {
  return result && result.ai_skipped === SUBSCRIPTION_EXPIRED ? REPORT_AI_SKIPPED_NOTE : null;
}
